import { Grid, InputLabel } from "@material-ui/core";
import {
  Button,
  Checkbox,
  FlashMessage,
  Input,
  Modal,
  ReadMoreLess,
  SelectNew,
} from "../../common";
import React, { useEffect, useMemo, useState } from "react";
import ProjectTitleIcon from "../icons/project-title-icon.svg";
import ExternalLinkIcon from "../icons/external-link.svg";
import DateIcon from "../icons/date-icon.svg";
import PinkPlus from "../icons/plus-pink.svg";
import DeleteIcon from "../icons/delete-icon.svg";
import EditIcon from "../icons/edit-icon.svg";
import JobTypeIcon from "../icons/project-type-icon.svg";

import { useFieldArray, useForm } from "react-hook-form";
import { updateCandidateProjectsRequest } from "../../../reducers/candidate/candidate.reducer";
import { jobTypesRequest } from "../../../reducers/job/jobs.reducer";
import { useDispatch, useSelector } from "react-redux";
import { rootReducersState } from "../../../reducers";
import _ from "lodash";
import { pluckFromArray, stripHTMLTags } from "../../../utils/helper";
import type { OptionsProp } from "../../common/SelectNew";
import RichTextEditor from "../../common/RichTextEditor";
import ProjectsIconAfter from "../icons/projects-icon-after.svg";

interface Project {
  title: string;
  description: string;
  link: string;
  start_date: string;
  end_date: string | null;
  is_present: number | boolean;
  company_name: string | null;
  /** Job type association from API */
  jobType?: { id: number; title: string } | null;
  /** FK stored on candidate_projects.type */
  type?: number | null;
  /** React-select value for SelectNew */
  job_type?: OptionsProp | null;
}

interface ProjectsSectionProps {
  projects: Project[];
  isPublicPage?: boolean;
  openRequest?: boolean;
  onOpenHandled?: () => void;
}

function ProjectsSection({ projects, isPublicPage, openRequest, onOpenHandled }: ProjectsSectionProps) {
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: { projects: projects || [] },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "projects",
  });

  const candidateViewFlag = useSelector(
    ({ candidate }: rootReducersState) => candidate.candidates.candidateViewFlag
  );
  const jobTypes = useSelector(
    ({ job }: rootReducersState) => job.jobs.jobTypes
  );
  const [localProjects, setLocalProjects] = useState<Project[]>([]);

  const dispatch = useDispatch();

  const jobTypeOptions = useMemo(() => {
    if (_.isEmpty(jobTypes)) return [];
    const raw = pluckFromArray(
      jobTypes,
      "id",
      "title",
      "value",
      "label"
    ) as OptionsProp[];
    return raw.map((o) => ({ ...o, value: String(o.value) }));
  }, [jobTypes]);

  useEffect(() => {
    if (_.isEmpty(jobTypes)) {
      dispatch(jobTypesRequest());
    }
  }, [dispatch, jobTypes]);
  const [projectModal, setProjectModal] = useState(false);

  const openModalWithProjects = () => {
    if (isPublicPage) return;
    const updatedProjects = watch("projects");
    reset({ projects: updatedProjects });
    setProjectModal(true);
  };

  useEffect(() => {
    if (!openRequest || isPublicPage) return;
    openModalWithProjects();
    onOpenHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRequest, isPublicPage]);

  const closeModalWithProjects = () => {
    const updatedProjects = watch("projects");
    reset({ projects: updatedProjects });
    setProjectModal(false);
  };

  useEffect(() => {
    if (!_.isEmpty(projects)) {
      const mappedProjects = projects.map((project) => {
        const jt = _.get(project, "jobType", null);
        const typeId = _.get(project, "type");
        const job_type =
          jt != null && _.get(jt, "id") != null
            ? {
                value: String(_.get(jt, "id")),
                label: _.get(jt, "title", "") || "",
              }
            : typeId !== undefined && typeId !== null && `${typeId}` !== ""
            ? {
                value: String(typeId),
                label:
                  String(
                    _.get(
                      _.find(
                        jobTypeOptions,
                        (o) => String(o.value) === String(typeId)
                      ),
                      "label",
                      ""
                    )
                  ) || "",
              }
            : null;

        return {
          ...project,
          is_present: Number(project.is_present) || 0,
          title: project.title || "",
          description: project.description || "",
          link: project.link || "",
          start_date: project.start_date || "",
          end_date: project.end_date || null,
          company_name: project.company_name || null,
          job_type,
        };
      });

      reset({ projects: mappedProjects });
      setLocalProjects(mappedProjects);
    } else {
      reset({ projects: [] });
      setLocalProjects([]);
    }
  }, [projects, reset, jobTypeOptions]);

  const onSubmit = (data: { projects: Project[] }) => {
    const updated = data?.projects?.map((project) => {
      const rawType = _.get(project, "job_type.value");
      const typeNum =
        rawType !== undefined && rawType !== null && rawType !== ""
          ? Number(rawType)
          : null;

      return {
        title: project.title,
        description: project.description,
        link: project.link,
        start_date: project.start_date,
        end_date: project.end_date,
        is_present: project.is_present ? 1 : 0,
        company_name: project.company_name,
        type:
          typeNum !== undefined &&
          typeNum !== null &&
          !Number.isNaN(typeNum)
            ? typeNum
            : null,
      };
    });

    const payload = {
      projects: updated,
    };
    dispatch(updateCandidateProjectsRequest(payload));
  };

  useEffect(() => {
    if (candidateViewFlag === "projects_success") {
      FlashMessage("Projects updated successfully");
      const updatedProjects = watch("projects");

      // Update local state
      setLocalProjects(updatedProjects);
      reset({ projects: updatedProjects });
      setProjectModal(false);
    }
    if (candidateViewFlag === "projects_failed") {
      FlashMessage("Failed to update projects", "error");
    }

    // eslint-disable-next-line
  }, [candidateViewFlag]);

  const _handlePresentCheckBoxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const { checked } = e.target;

    setValue(`projects.${index}.is_present`, checked ? 1 : 0);

    if (checked) {
      // Hide end_date if present is true
      setValue(`projects.${index}.end_date`, null);
    } else {
      // Optionally set a default or leave it blank
      setValue(`projects.${index}.end_date`, "");
    }
  };

  const maxLimit = 1000;

  return (
    <div className="accordion" id="profile-projects">
      <div className="accordion-head">
        <b>Projects</b>
        {!isPublicPage &&
          (!_.isEmpty(localProjects) ? (
            <div className="edit-icon" onClick={openModalWithProjects}>
              <img src={EditIcon} alt="edit" />
            </div>
          ) : (
            <span onClick={openModalWithProjects}>+Add</span>
          ))}
      </div>
      {!_.isEmpty(localProjects) && (
        <div className="accordion-body">
          <div
            className={`projects-content${
              isPublicPage ? " main-public-page profile-timeline-rail" : ""
            }`}
          >
            {localProjects?.map((project, index) => (
              <div
                key={index}
                className={`project-item${isPublicPage ? " profile-timeline-item" : ""}`}
              >
                {isPublicPage && (
                  <span
                    className="profile-timeline-icon profile-timeline-icon--tall"
                    aria-hidden="true"
                  >
                    <img src={ProjectsIconAfter} alt="" />
                  </span>
                )}
                <h5>{project?.title}</h5>
                {(_.get(project, "jobType.title") ||
                  _.get(project, "job_type.label")) && (
                  <span className="project-job-type-label">
                    {_.get(project, "jobType.title") ||
                      _.get(project, "job_type.label")}
                  </span>
                )}
                {
                  project?.description && (
                    <div className="bordered-content wysiwyg-data">
                      <ReadMoreLess text={project.description} textLimit={300} />
                    </div>
                  )
                }
              </div>
            ))}
          </div>
        </div>
      )}
      {!isPublicPage && (
        <Modal
          visible={projectModal}
          onClose={() => closeModalWithProjects()}
          className={`candidate-profile-modal project-modal`}
          title={!_.isEmpty(localProjects) ? "Edit Projects" : "Projects"}
        >
        <div className="modal-body-wrapper">
          <form onSubmit={handleSubmit(onSubmit)}>
            {!_.isEmpty(fields) ? (
              fields.map((item, index) => (
                <div className="form-wrapper" key={item.id}>
                  <Grid container spacing={2}>
                  <Grid item sm={6} xs={12}>
                  <div className="form-group">
                    <Input
                      inputIcon={
                        <div className="input-icon">
                          <img src={ProjectTitleIcon} alt="icon" />
                        </div>
                      }
                      className="input-with-icon"
                      externalLabel={{ label: "Project Title", className: "required"}}
                      placeholder="Project title"
                      validationObj={errors}
                      defaultValue={item.title}
                      {...register(`projects.${index}.title`, {
                        required: {
                          value: true,
                          message: "Please enter the Project title.",
                        },
                      })}
                    />
                  </div>
                  </Grid>
                  <Grid item sm={6} xs={12}>
                  <div className="form-group">
                    <SelectNew
                      name={`projects.${index}.job_type`}
                      externalLabel={{
                        label: "Job Type",
                        className: "required",
                      }}
                      placeholder="Select job type"
                      options={jobTypeOptions}
                      search
                      icon={<img src={JobTypeIcon} alt="" />}
                      validationObj={errors}
                      control={control}
                      required={true}
                      rules={{
                        required: {
                          value: true,
                          message: "Please select a job type",
                        },
                      }}
                    />
                  </div>
                  </Grid>
                  </Grid>
                  <div className="form-group">
                    <Input
                      inputIcon={
                        <div className="input-icon">
                          <img src={ExternalLinkIcon} alt="icon" />
                        </div>
                      }
                      className="input-with-icon"
                      externalLabel={{ label: "Project URL", className: "required"}}
                      placeholder="Project URL"
                      validationObj={errors}
                      defaultValue={item.link}
                      {...register(`projects.${index}.link`, {
                        required: {
                          value: true,
                          message: "Please enter the Project URL.",
                        },
                      })}
                    />
                  </div>

                  <Grid container spacing={2}>
                    <Grid item sm={6} xs={12}>
                      <div className="form-group">
                        <Input
                          inputIcon={
                            <div className="input-icon">
                              <img src={DateIcon} alt="icon" />
                            </div>
                          }
                          type="date"
                          className="input-with-icon"
                          externalLabel={{ label: "Start Date", className: "required"}}
                          placeholder="Start Date"
                          validationObj={errors}
                          defaultValue={item.start_date}
                          inputProps={{
                            max: new Date().toISOString().split('T')[0]
                          }}
                          {...register(`projects.${index}.start_date`, {
                            required: {
                              value: true,
                              message: "Please enter the Start Date.",
                            },
                            validate: (value) => {
                              if (!value) return true;
                              const today = new Date().toISOString().split('T')[0];
                              if (value > today) {
                                return "Start date cannot be in the future";
                              }
                              return true;
                            },
                          })}
                        />
                      </div>
                    </Grid>

                    <Grid item sm={6} xs={12}>
                      <div className="form-group">
                         {watch(`projects.${index}.is_present`) ? (
                          <InputLabel className="present-label">Present</InputLabel>
                         ) : (
                          <Input
                            inputIcon={
                              <div className="input-icon">
                                <img src={DateIcon} alt="icon" />
                              </div>
                            }
                            type="date"
                            className="input-with-icon"
                            externalLabel={{ label: "End Date"}}
                            placeholder="End Date"
                            validationObj={errors}
                            defaultValue={item.end_date ?? ""}
                            inputProps={{
                              min: getValues(`projects.${index}.start_date`) || item.start_date || "",
                              max: new Date().toISOString().split('T')[0]
                            }}
                            {...register(`projects.${index}.end_date`, {
                              required: {
                                value: false,
                                message: "Please enter the End Date.",
                              },
                              validate: (value) => {
                                if (!value) return true; // Allow empty if not required
                                const today = new Date().toISOString().split('T')[0];
                                if (value > today) {
                                  return "End date cannot be in the future";
                                }
                                const startDate = getValues(`projects.${index}.start_date`) || item.start_date;
                                if (startDate && value < startDate) {
                                  return "End date cannot be less than start date";
                                }
                                return true;
                              },
                            })}
                            disabled={watch(`projects.${index}.is_present`)}
                          />
                          )}
                      </div>
                    </Grid>
                  </Grid>

                  <div className="form-group">
                    <Checkbox
                      externalLabel={{ label: "Present", className: "required"}}
                      {...register(`projects.${index}.is_present`)}
                      defaultChecked={
                        item?.is_present === 1 || item?.is_present === true
                      }
                      onChange={(e: any) =>
                        _handlePresentCheckBoxChange(e, index)
                      }
                    />
                  </div>
                  <div className="form-group">
                    <RichTextEditor
                      key={item.id}
                      name={`projects.${index}.description`}
                      defaultValue={item.description || ""}
                      externalLabel={{ label: "Project Description", className: "required"}}
                      placeholder="Describe your project, technologies used, your role, and key achievements..."
                      validationObj={errors}
                      control={control}
                      rules={{
                        validate: {
                          validLength: (value: any) => {
                            const tmpText = stripHTMLTags(value || "");
                            if (tmpText.length > 1000) {
                              return "Can not exceed more then 1000 characters";
                            }
                            return true;
                          },
                        },
                        required: {
                          value: true,
                          message: "Please enter project description",
                        },
                      }}
                      showCount={true}
                      maxLimit={maxLimit}
                      counterAlign="right"
                      useMention={false}
                      AIflag={false}
                    />
                  </div>
                  <div className="action-wrapper">
                    <div
                      className="icon"
                      onClick={() =>
                        append({
                          title: "",
                          link: "",
                          start_date: "",
                          end_date: "",
                          is_present: 0,
                          description: "",
                          company_name: null,
                          job_type: null,
                        })
                      }
                    >
                      <img src={PinkPlus} alt="Add" />
                    </div>
                    <div className="icon" onClick={() => remove(index)}>
                      <img src={DeleteIcon} alt="Delete" />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <Button
                type="button"
                className="add-new-btn"
                onClick={() =>
                  append({
                    title: "",
                    link: "",
                    start_date: "",
                    end_date: "",
                    is_present: 0,
                    description: "",
                    company_name: null,
                    job_type: null,
                  })
                }
              >
                Add new
              </Button>
            )}
            <div className="btn-wrapper">
              <Button
                variant="outlined"
                className="outlined"
                onClick={() => closeModalWithProjects()}
                color="none"
              >
                Cancel
              </Button>
              <Button className="save-btn" type="submit">
                Submit
              </Button>
            </div>
          </form>
        </div>
        </Modal>
      )}
    </div>
  );
}

export default ProjectsSection;
